import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2,
  Monitor,
  Search,
  Globe2,
  Shield,
  ShieldOff,
  Trash2,
  Activity,
  Fingerprint,
  Clock,
  User as UserIcon,
  Copy,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AdminNav } from "@/components/admin/AdminNav";
import { AdminOnly } from "@/components/admin/AdminOnly";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { adminListDevices, adminSetDeviceActive, adminDeleteDevice, adminListUsersWithoutDevices } from "@/lib/admin-security.functions";
import { parseDeviceInfo } from "@/lib/device-id";

export const Route = createFileRoute("/_authenticated/admin/devices")({
  head: () => ({ meta: [{ title: "Devices — Admin" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <AdminOnly>
      <AdminNav />
      <DevicesPage />
    </AdminOnly>
  ),
});

type DeviceRow = {
  id: string;
  user_id: string;
  device_id: string;
  device_name: string | null;
  ip_address: string | null;
  location: string | null;
  user_agent: string | null;
  first_seen_at: string;
  last_seen_at: string;
  is_active: boolean;
  profiles: { email: string; full_name: string | null } | null;
};

function relTime(iso: string) {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

function DevicesPage() {
  const [search, setSearch] = useState("");
  const [onlyActive, setOnlyActive] = useState(true);
  const [copied, setCopied] = useState<string | null>(null);
  const qc = useQueryClient();
  const run = useServerFn(adminListDevices);
  const runNoDev = useServerFn(adminListUsersWithoutDevices);
  const runActive = useServerFn(adminSetDeviceActive);
  const runDelete = useServerFn(adminDeleteDevice);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-devices", search, onlyActive],
    queryFn: () => run({ data: { search, onlyActive, limit: 300 } }),
  });
  const { data: noDevData } = useQuery({
    queryKey: ["admin-users-no-devices"],
    queryFn: () => runNoDev(),
  });


  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin-devices"] });
  const setActive = useMutation({
    mutationFn: (v: { id: string; active: boolean }) =>
      runActive({ data: { deviceRowId: v.id, active: v.active } }),
    onSuccess: () => { toast.success("Updated"); invalidate(); },
    onError: (e: Error) => toast.error(e.message),
  });
  const remove = useMutation({
    mutationFn: (id: string) => runDelete({ data: { deviceRowId: id } }),
    onSuccess: () => { toast.success("Deleted"); invalidate(); },
    onError: (e: Error) => toast.error(e.message),
  });

  const rows = (data?.devices ?? []) as DeviceRow[];

  const stats = useMemo(() => {
    const total = rows.length;
    const active = rows.filter((r) => r.is_active).length;
    const uniqueIps = new Set(rows.map((r) => r.ip_address).filter(Boolean) as string[]).size;
    const missing = rows.filter((r) => !r.ip_address).length;
    return { total, active, uniqueIps, missing };
  }, [rows]);

  const copyIp = async (ip: string) => {
    try {
      await navigator.clipboard.writeText(ip);
      setCopied(ip);
      toast.success("IP copied");
      setTimeout(() => setCopied((c) => (c === ip ? null : c)), 1600);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        icon={Monitor}
        title="Devices"
        subtitle="Every device that has ever signed in. IP is captured on each sign-in — older sessions may show as pending until the user signs in again."
      />

      {/* Stats strip */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile icon={Monitor} label="Total devices" value={stats.total} tone="indigo" />
        <StatTile icon={Activity} label="Active" value={stats.active} tone="emerald" />
        <StatTile icon={Globe2} label="Unique IPs" value={stats.uniqueIps} tone="violet" />
        <StatTile icon={ShieldOff} label="Pending IP" value={stats.missing} tone="amber" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border bg-card/60 p-3 backdrop-blur">
        <div className="relative min-w-[240px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search device / owner / IP / location…"
            className="h-10 ps-9"
          />
        </div>
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border bg-background px-3 py-1.5 text-xs">
          <input
            type="checkbox"
            checked={onlyActive}
            onChange={(e) => setOnlyActive(e.target.checked)}
            className="accent-indigo-500"
          />
          Only active
        </label>
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center rounded-2xl border bg-card py-16 text-sm text-muted-foreground">
          <Loader2 className="me-2 h-4 w-4 animate-spin" /> Loading devices…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border bg-card py-16 text-center text-sm text-muted-foreground">
          No devices match your filters.
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((d) => {
            const info = parseDeviceInfo(d.user_agent ?? "");
            const ip = d.ip_address;
            const isCopied = copied === ip;
            return (
              <div
                key={d.id}
                className="group relative overflow-hidden rounded-2xl border bg-gradient-to-br from-card to-card/60 p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg"
              >
                {/* Aurora accent */}
                <div
                  aria-hidden
                  className={`pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full blur-3xl transition-opacity ${
                    d.is_active ? "bg-emerald-500/15" : "bg-slate-500/10"
                  } group-hover:opacity-80`}
                />

                {/* Header row */}
                <div className="relative flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-indigo-500/15 to-violet-500/15 text-indigo-500 ring-1 ring-indigo-500/20">
                      <Monitor className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold">
                        {d.device_name ?? info.name}
                      </div>
                      <div className="mt-0.5 text-[11px] text-muted-foreground">
                        {info.browser} · {info.os}
                      </div>
                    </div>
                  </div>

                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                      d.is_active
                        ? "bg-emerald-500/10 text-emerald-500 ring-1 ring-emerald-500/20"
                        : "bg-muted text-muted-foreground ring-1 ring-border"
                    }`}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${
                        d.is_active ? "animate-pulse bg-emerald-500" : "bg-muted-foreground/50"
                      }`}
                    />
                    {d.is_active ? "active" : "inactive"}
                  </span>
                </div>

                {/* Owner */}
                <div className="relative mt-4 flex items-center gap-2 rounded-lg border bg-background/60 px-3 py-2">
                  <UserIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-medium">
                      {d.profiles?.full_name ?? "—"}
                    </div>
                    <div className="truncate text-[11px] text-muted-foreground">
                      {d.profiles?.email ?? "unknown"}
                    </div>
                  </div>
                </div>

                {/* IP block */}
                <div className="relative mt-3 rounded-lg border bg-gradient-to-br from-indigo-500/[0.04] to-violet-500/[0.04] p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      <Globe2 className="h-3.5 w-3.5 text-indigo-500" />
                      IP address
                    </div>
                    {ip ? (
                      <button
                        onClick={() => copyIp(ip)}
                        className="rounded-md p-1 text-muted-foreground opacity-0 transition hover:bg-muted hover:text-foreground group-hover:opacity-100"
                        aria-label="Copy IP"
                      >
                        {isCopied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                      </button>
                    ) : null}
                  </div>
                  <div className="mt-1 font-mono text-sm font-semibold tracking-tight">
                    {ip ?? <span className="text-[11px] font-normal italic text-muted-foreground">Pending — will appear on next sign-in</span>}
                  </div>
                  {d.location ? (
                    <div className="mt-1 text-[11px] text-muted-foreground">{d.location}</div>
                  ) : null}
                </div>

                {/* Meta row */}
                <div className="relative mt-3 grid grid-cols-2 gap-2 text-[11px] text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-3 w-3" />
                    <span>Last seen {relTime(d.last_seen_at)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 truncate">
                    <Fingerprint className="h-3 w-3" />
                    <span className="truncate font-mono" title={d.device_id}>
                      {d.device_id.slice(0, 10)}…
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="relative mt-4 flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 flex-1 text-xs"
                    onClick={() => setActive.mutate({ id: d.id, active: !d.is_active })}
                    disabled={setActive.isPending}
                  >
                    {d.is_active ? (
                      <><ShieldOff className="me-1 h-3.5 w-3.5" /> Deactivate</>
                    ) : (
                      <><Shield className="me-1 h-3.5 w-3.5" /> Reactivate</>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    className="h-8 text-xs"
                    onClick={() => {
                      if (confirm("Delete this device row? The user will need to sign in again on this device."))
                        remove.mutate(d.id);
                    }}
                    disabled={remove.isPending}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Users without any recorded device */}
      {noDevData?.users && noDevData.users.length > 0 ? (
        <div className="rounded-2xl border bg-card/60 p-4 backdrop-blur">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold">Users without a recorded device</div>
              <div className="text-[11px] text-muted-foreground">
                These accounts existed before device tracking or haven't signed in since. Their device + IP will be captured on the next sign-in.
              </div>
            </div>
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
              {noDevData.users.length}
            </span>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {noDevData.users.map((u) => (
              <div key={u.id} className="flex items-center gap-2 rounded-lg border bg-background/60 px-3 py-2">
                <UserIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs font-medium">{u.full_name || "—"}</div>
                  <div className="truncate text-[11px] text-muted-foreground">{u.email}</div>
                </div>
                <span className="rounded-full border px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                  {u.role}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}


function StatTile({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: typeof Monitor;
  label: string;
  value: number;
  tone: "indigo" | "emerald" | "violet" | "amber";
}) {
  const toneMap = {
    indigo: "from-indigo-500/15 to-indigo-500/5 text-indigo-500 ring-indigo-500/20",
    emerald: "from-emerald-500/15 to-emerald-500/5 text-emerald-500 ring-emerald-500/20",
    violet: "from-violet-500/15 to-violet-500/5 text-violet-500 ring-violet-500/20",
    amber: "from-amber-500/15 to-amber-500/5 text-amber-500 ring-amber-500/20",
  } as const;
  return (
    <div className="relative overflow-hidden rounded-2xl border bg-card p-4">
      <div
        aria-hidden
        className={`pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full bg-gradient-to-br blur-2xl ${toneMap[tone]}`}
      />
      <div className="relative flex items-center gap-3">
        <div className={`grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br ring-1 ${toneMap[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
          <div className="text-2xl font-black tabular-nums">{value}</div>
        </div>
      </div>
    </div>
  );
}
